import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { RootState } from '@store/types';
import { AirflowDashboardHandler } from '@api/airflow-dashboard-handler';
import { errorAlert } from '@components/toastify/notify-toast';
import { AIRFLOW_BASE_PATH, DAG_TASK_DETAIL_TAB, DagTaskDetailTab } from '../constants';
import { AirflowTaskDefinition } from '../types';

/*
 * Manages the DAG-task detail screen's header data, active tab, and refresh propagation
 */
export function useDagTaskDetail() {
    // react-router-dom v5's useParams does not decode dynamic segments — dagId/taskId arrive
    // exactly as they appeared in the URL (still percent-encoded), so encoding them again for
    // an API call would double-encode. Decode once here, right at the source.
    const { dagId: rawDagId, taskId: rawTaskId } = useParams<{ dagId: string; taskId: string }>();
    const dagId = decodeURIComponent(rawDagId);
    const taskId = decodeURIComponent(rawTaskId);
    const { envVariables: Env } = useSelector((store: RootState) => store.AccountReducer);
    const history = useHistory();
    const location = useLocation();

    const [task, setTask] = useState<AirflowTaskDefinition | null>(null);
    const [isLoadingTask, setIsLoadingTask] = useState(true);
    const [refreshKey, setRefreshKey] = useState(0);

    const activeTab = useMemo<DagTaskDetailTab>(() => {
        const params = new URLSearchParams(location.search);
        return (params.get('tab') as DagTaskDetailTab) || DAG_TASK_DETAIL_TAB.overview;
    }, [location.search]);

    const handleTabChange = useCallback(
        (tab: string) => {
            const params = new URLSearchParams(location.search);
            params.set('tab', tab);
            history.replace({ pathname: location.pathname, search: params.toString() });
        },
        [history, location.pathname, location.search]
    );

    const fetchTask = useCallback(async () => {
        try {
            const response = await AirflowDashboardHandler.getDagTask(Env, dagId, taskId);
            setTask(response);
        } catch {
            errorAlert(`Failed to load task "${taskId}".`);
        } finally {
            setIsLoadingTask(false);
        }
    }, [Env, dagId, taskId]);

    useEffect(() => {
        fetchTask();
    }, [dagId, taskId]);

    /*
     * Re-fetches the task and bumps the refresh key every tab watches
     */
    const handleRefresh = useCallback(() => {
        fetchTask();
        setRefreshKey((key) => key + 1);
    }, [fetchTask]);

    /*
     * Navigates back to the DAG detail screen's Tasks tab
     */
    const handleBack = useCallback(() => {
        history.push(`${AIRFLOW_BASE_PATH}/${encodeURIComponent(dagId)}?tab=tasks`);
    }, [history, dagId]);

    /*
     * Navigates to the parent DAG's detail screen (breadcrumb's DAG ID link)
     */
    const handleDagClick = useCallback(() => {
        history.push(`${AIRFLOW_BASE_PATH}/${encodeURIComponent(dagId)}`);
    }, [history, dagId]);

    /*
     * Navigates to the parent DAG's Runs tab (breadcrumb's "All Runs" link)
     */
    const handleRunsClick = useCallback(() => {
        history.push(`${AIRFLOW_BASE_PATH}/${encodeURIComponent(dagId)}?tab=runs`);
    }, [history, dagId]);

    return {
        dagId,
        taskId,
        task,
        isLoadingTask,
        refreshKey,
        activeTab,
        handleTabChange,
        handleRefresh,
        handleBack,
        handleDagClick,
        handleRunsClick,
    };
}
